import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFn } from '@/features/learning/lib/i18n';
import { Card, CardContent, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { GraduationCap, Loader2, Plus, Award, ClipboardCheck, ClipboardList, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import * as api from '@/services/learningService';
import { getApiErrorMessage } from '@/lib/apiError';
import { usePeopleDirectory } from '@/features/learning/lib/usePeopleDirectory';
import { PersonPicker } from '@/features/shared/components/ConditionGroupBuilder';
import { useProgramReadiness, usePrograms, useSessions, useTrackSummary } from '@/features/learning/api/hooks';
import { dayInZone, formatSessionRange, isoDaysFromNow } from '@/features/learning/lib/format';
import { endsToday } from '@/features/learning/lib/attendance';
import { SessionStatusChip } from '@/features/learning/components/SessionStatusChip';
import { SessionDetailSheet, type SheetSection } from '@/features/learning/components/SessionDetailSheet';
import { ValidationQueue } from '@/features/learning/components/ValidationQueue';
import { ReadinessSummary } from '@/features/learning/components/ReadinessSummary';

// Team Learning — the people-manager lane (anyone holding learning:assign, via
// role or a custom grant): assign courses from the catalog to their people,
// follow their team's progress, and verify completions. The backend narrows
// every list and write to the caller's reachable set (org-scope grants:
// reporting subtree for team scope, fail-closed to self), so this page never
// needs — and never gets — the tenant-wide admin surface. Catalog management
// and requirement rules stay under Administration → Learning (learning:manage).
export const TeamLearningPage: React.FC = () => {
  const { t: t0 } = useTranslation(['training']);
  const t = t0 as unknown as TFn;
  const [courses, setCourses] = useState<api.Course[]>([]);
  const [team, setTeam] = useState<api.Assignment[]>([]);
  const [certs, setCerts] = useState<api.Certification[]>([]);
  const [busy, setBusy] = useState(false);
  const [assignDraft, setAssignDraft] = useState<any>(null);
  // Sessions from the start of today to +30 days (upcoming / in progress) plus the
  // last 14 days (attendance summary) — the backend narrows both to the caller's reach.
  const [window] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return { pastFrom: isoDaysFromNow(-14), from: today.toISOString(), to: isoDaysFromNow(30) };
  });
  const upcomingQ = useSessions(window.from, window.to);
  const pastQ = useSessions(window.pastFrom, window.from);
  const upcomingSessions = useMemo(() => [...(upcomingQ.data ?? [])].filter((s) => s.status !== 'cancelled').sort((a, b) => a.starts_at.localeCompare(b.starts_at)), [upcomingQ.data]);
  const pastSessions = useMemo(() => [...(pastQ.data ?? [])].filter((s) => s.status !== 'cancelled' && s.status !== 'draft').sort((a, b) => b.starts_at.localeCompare(a.starts_at)), [pastQ.data]);
  const [openSession, setOpenSession] = useState<{ session: api.Session; section?: SheetSection } | null>(null);
  /** In progress, or completed today → attendance can be taken now. */
  const canTakeAttendance = (s: api.Session) => s.status === 'in_progress' || (s.status === 'completed' && endsToday(s.ends_at));
  const sessionOptions = useMemo(() => [...pastSessions, ...upcomingSessions].map((s) => ({ id: s.id, title: s.title })), [pastSessions, upcomingSessions]);
  // Phase 5: team readiness — programs carrying a readiness policy; GET /readiness narrows people to my reach.
  const programsQ = usePrograms();
  const readinessPrograms = useMemo(() => [...(programsQ.data ?? [])].filter((p) => p.readiness_policy?.enabled && p.status !== 'archived' && p.status !== 'draft').sort((a, b) => a.name.localeCompare(b.name)), [programsQ.data]);
  const [readinessProgramId, setReadinessProgramId] = useState('');
  const readinessId = readinessProgramId || readinessPrograms[0]?.id || '';
  const readinessQ = useProgramReadiness(readinessId || null);

  const load = () => {
    api.listCourses().then(setCourses).catch(() => setCourses([]));
    api.listAssignments(true).then(setTeam).catch(() => setTeam([]));
    api.listCertifications(true).then(setCerts).catch(() => setCerts([]));
  };
  useEffect(() => { load(); }, []);
  // People with a user account — shared, paged fetch (the backend declines assignments outside my reach).
  const directory = usePeopleDirectory(true);
  const people = directory.data ?? [];

  const nameOf = (uid: string) => people.find((p) => p.user_id === uid)?.name || uid.slice(0, 8);
  const statusChip: Record<string, string> = {
    assigned: 'bg-muted text-muted-foreground', in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
    completed: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', verified: 'bg-success-soft text-success-strong', cancelled: 'bg-muted text-muted-foreground',
  };
  const open = team.filter((a) => a.status === 'assigned' || a.status === 'in_progress');
  const toVerify = team.filter((a) => a.status === 'completed');
  const overdue = useMemo(() => open.filter((a) => a.due_date && new Date(a.due_date) < new Date()).length, [open]);

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('training:team_title', 'Team Learning')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('training:team_subtitle', "Assign training to your people, follow their progress, and verify completions.")}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[{ icon: GraduationCap, label: t('training:team_open', 'Open on my team'), v: open.length },
          { icon: Loader2, label: t('training:team_overdue', 'Overdue'), v: overdue },
          { icon: Award, label: t('training:team_to_verify', 'Awaiting verification'), v: toVerify.length }].map((s, i) => (
          <Card key={i}><CardContent className="pt-4 flex items-center gap-3">
            <s.icon className="w-6 h-6 text-orange-500" />
            <div><div className="text-xl font-bold tabular-nums">{s.v}</div><div className="text-[11px] text-muted-foreground">{s.label}</div></div>
          </CardContent></Card>
        ))}
      </div>

      {/* Upcoming sessions for my team (Programs + Sessions) */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base">{t('training:team_upcoming_sessions', 'Upcoming sessions for my team')}</CardTitle>
        {upcomingQ.isLoading && <p className="text-sm text-muted-foreground">{t('training:loading', 'Loading')}…</p>}
        {!upcomingQ.isLoading && upcomingSessions.length === 0 && <p className="text-sm text-muted-foreground">{t('training:team_no_sessions', 'No sessions scheduled in the next 30 days.')}</p>}
        {upcomingSessions.map((s) => (
          <div key={s.id} className="rounded-md border border-border p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{s.title}</span>
                <SessionStatusChip status={s.status} />
              </div>
              <div className="text-xs text-muted-foreground">
                {s.program_name ? `${s.program_name} · ` : ''}{formatSessionRange(s.starts_at, s.ends_at, undefined, s.timezone)} · {t('training:participants_n', '{{n}} participant(s)', { n: s.participant_count })}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {canTakeAttendance(s) && (
                <Button size="sm" onClick={() => setOpenSession({ session: s, section: 'attendance' })} data-testid={`take-attendance-${s.id}`}>
                  <ClipboardCheck className="mr-1 h-4 w-4" aria-hidden />{t('training:take_attendance', 'Take attendance')}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setOpenSession({ session: s })}>{t('training:view', 'View')}</Button>
            </div>
          </div>
        ))}
      </CardContent></Card>

      {/* Recent sessions — attendance summary (server-computed per track, summed here for display only) */}
      {pastSessions.length > 0 && (
        <Card><CardContent className="pt-4 space-y-2.5">
          <CardTitle className="text-base">{t('training:team_recent_sessions', 'Recent sessions')}</CardTitle>
          {pastSessions.map((s) => (
            <div key={s.id} className="rounded-md border border-border p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{s.title}</span>
                  <SessionStatusChip status={s.status} />
                  <SessionAttendanceSummary sessionId={s.id} t={t} />
                </div>
                <div className="text-xs text-muted-foreground">
                  {s.program_name ? `${s.program_name} · ` : ''}{formatSessionRange(s.starts_at, s.ends_at, undefined, s.timezone)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {canTakeAttendance(s) && (
                  <Button size="sm" onClick={() => setOpenSession({ session: s, section: 'attendance' })} data-testid={`take-attendance-${s.id}`}>
                    <ClipboardCheck className="mr-1 h-4 w-4" aria-hidden />{t('training:take_attendance', 'Take attendance')}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setOpenSession({ session: s, section: 'attendance' })}>{t('training:attendance', 'Attendance')}</Button>
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}

      {/* Phase 5: team go-live readiness (server-computed, narrowed to my reach) */}
      {readinessPrograms.length > 0 && (
        <Card data-testid="team-readiness"><CardContent className="pt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base inline-flex items-center gap-2"><Target className="h-4 w-4 text-orange-500" aria-hidden />{t('training:team_readiness', 'Team readiness')}</CardTitle>
            <select aria-label={t('training:ov_programs', 'Programs')} className="h-8 rounded-md border border-border bg-background px-2 text-sm" value={readinessId} onChange={(e) => setReadinessProgramId(e.target.value)}>
              {readinessPrograms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-muted-foreground">{t('training:team_readiness_help', 'Who on your team is go-live ready for this program, and what still blocks the others.')}</p>
          {readinessQ.isLoading && <p className="text-sm text-muted-foreground">{t('training:loading', 'Loading')}…</p>}
          {readinessQ.isError && <p className="text-sm text-red-600 dark:text-red-400">{getApiErrorMessage(readinessQ.error, t('training:err', 'Something went wrong'))}</p>}
          {readinessQ.data && <ReadinessSummary data={readinessQ.data} />}
        </CardContent></Card>
      )}

      {/* Phase 4: practical work awaiting my validation (the API narrows rows to my reach and manager-kind exercises) */}
      <Card data-testid="team-validation-queue"><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base inline-flex items-center gap-2"><ClipboardList className="h-4 w-4 text-orange-500" aria-hidden />{t('training:practice_queue', 'Practical work to validate')}</CardTitle>
        <p className="text-[11px] text-muted-foreground">{t('training:team_practice_help', 'Exercises your people submitted that need a manager to validate. Open the session for the full completion picture.')}</p>
        <ValidationQueue defaultStatus="submitted" lockStatus sessionOptions={sessionOptions} />
      </CardContent></Card>

      {/* Assign */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('training:team_assign_title', 'Assign training')}</CardTitle>
          <Button size="sm" onClick={() => setAssignDraft({})}><Plus className="mr-1 h-4 w-4" />{t('training:assign', 'Assign')}</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">{t('training:team_assign_help', 'Pick a course from the company catalog. You can assign to people you manage; assignments outside your team are declined.')}</p>
        {assignDraft && (
          <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-3 grid sm:grid-cols-3 gap-2 items-end">
            <div className="space-y-1"><Label className="text-xs">{t('training:course', 'Course')}</Label>
              <select className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm" value={assignDraft.course_id || ''} onChange={(e) => setAssignDraft({ ...assignDraft, course_id: e.target.value })}>
                <option value="">{t('training:pick_course', 'Select…')}</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select></div>
            <div className="space-y-1"><Label htmlFor="team-assignee" className="text-xs">{t('training:assignee', 'Assignee')}</Label>
              <PersonPicker id="team-assignee" className="" multiple={false} people={people} value={assignDraft.user_id ? [assignDraft.user_id] : []}
                labels={{ searchPeople: t('training:search_people_ph', 'Type a name or department…'), noMatches: t('training:no_people_match', 'No one matches.'), selected: t('training:selected', 'Selected'), remove: t('training:remove', 'Remove') }}
                onChange={(ids) => setAssignDraft({ ...assignDraft, user_id: ids[0] || '' })} />
              {directory.isError && <p className="text-[11px] text-red-600 dark:text-red-400">{t('training:people_load_failed', 'Could not load the employee directory — reload and try again.')}</p>}
            </div>
            <div className="space-y-1"><Label className="text-xs">{t('training:due_date', 'Due date (optional)')}</Label>
              <Input className="h-8" type="date" value={assignDraft.due_date || ''} onChange={(e) => setAssignDraft({ ...assignDraft, due_date: e.target.value })} /></div>
            <div className="sm:col-span-3 flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setAssignDraft(null)}>{t('training:cancel', 'Cancel')}</Button>
              <Button size="sm" disabled={busy || !assignDraft.user_id || !assignDraft.course_id} onClick={async () => {
                setBusy(true);
                try {
                  const emp = people.find((p) => p.user_id === assignDraft.user_id);
                  await api.assignCourse({ course_id: assignDraft.course_id, user_id: assignDraft.user_id, employee_id: emp?.id, due_date: assignDraft.due_date ? new Date(assignDraft.due_date).toISOString() : undefined });
                  toast.success(t('training:assigned_ok', 'Assigned — they have been notified'));
                  setAssignDraft(null); load();
                } catch (e: any) { toast.error(getApiErrorMessage(e, t('training:err', 'Something went wrong'))); }
                finally { setBusy(false); }
              }}>{t('training:assign', 'Assign')}</Button>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Team progress */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base">{t('training:team_progress', "My team's training")}</CardTitle>
        {team.length === 0 && <p className="text-sm text-muted-foreground">{t('training:team_none', 'No training on your team yet.')}</p>}
        {team.map((a) => (
          <div key={a.id} className="rounded-md border border-border p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
              <span className="font-medium">{a.course_title}</span>
              <Badge className={`ml-2 border-0 text-[10px] ${statusChip[a.status] || ''}`}>{a.status.replace('_', ' ')}</Badge>
              <div className="text-xs text-muted-foreground">
                {nameOf(a.user_id)}
                {a.due_date ? ` · ${t('training:due', 'Due')} ${formatDate(dayInZone(a.due_date, a.session_timezone))}` : ''}
                {a.evidence_url ? <a className="ml-2 text-orange-600 hover:underline" href={a.evidence_url} target="_blank" rel="noreferrer">{t('training:view_evidence', 'View evidence')}</a> : null}
              </div>
            </div>
            {a.status === 'completed' && (
              <Button size="sm" disabled={busy} onClick={async () => {
                setBusy(true);
                try { await api.verifyAssignment(a.id); toast.success(t('training:verified_ok', 'Verified')); load(); }
                catch (e: any) { toast.error(getApiErrorMessage(e, t('training:err', 'Something went wrong'))); }
                finally { setBusy(false); }
              }}>{t('training:verify', 'Verify')}</Button>
            )}
          </div>
        ))}
      </CardContent></Card>

      {/* Team certifications */}
      <Card><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base">{t('training:team_certs', 'Team certifications')}</CardTitle>
        {certs.length === 0 && <p className="text-sm text-muted-foreground">{t('training:team_no_certs', 'No certifications on your team yet.')}</p>}
        {certs.map((c) => {
          const expiring = c.expires_on && new Date(c.expires_on) < new Date(Date.now() + 30 * 86400000);
          return (
            <div key={c.id} className="text-sm flex flex-wrap justify-between gap-2 rounded-md border border-border p-2.5">
              <span className="font-medium">{c.name} <span className="font-normal text-xs text-muted-foreground">· {nameOf(c.user_id)}</span></span>
              <span className={`text-xs ${expiring ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                {c.issuer || ''}{c.expires_on ? ` · ${t('training:expires', 'expires')} ${formatDate(c.expires_on)}` : ''}
              </span>
            </div>
          );
        })}
      </CardContent></Card>

      <SessionDetailSheet sessionId={openSession?.session.id ?? null} initial={openSession?.session ?? null} section={openSession?.section} onClose={() => setOpenSession(null)} />
    </div>
  );
};

/** "attended/total" for a past session — GET /track-summary, summed across tracks (display only). */
const SessionAttendanceSummary: React.FC<{ sessionId: string; t: TFn }> = ({ sessionId, t }) => {
  const q = useTrackSummary(sessionId);
  if (!q.data || q.data.length === 0) return null;
  const total = q.data.reduce((n, r) => n + r.count, 0);
  const attended = q.data.reduce((n, r) => n + r.attended, 0);
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums" data-testid="attendance-summary">
      {t('training:attended_of_total', '{{attended}}/{{total}} attended', { attended, total })}
    </span>
  );
};

export default TeamLearningPage;
